import type { AxiosInstance } from 'axios';
import { msalInstance } from '@/authSetup.ts';
import type { ServiceSuccessResponse } from '@/models/common/serviceResponse';
import { fromAxios } from '@/models/common/serviceResponse';

import {
  DealersSchema,
  MsalAccountCodec,
  type User,
} from '@/models/entities/user.ts';

export interface IUserService {
  getCurrentUser(): Promise<ServiceSuccessResponse<User>>;
}

class UserService implements IUserService {
  private readonly axios: AxiosInstance;
  private cachedUserPromise: Promise<ServiceSuccessResponse<User>> | null =
    null;

  constructor(axios: AxiosInstance) {
    this.axios = axios;
  }

  getCurrentUser(): Promise<ServiceSuccessResponse<User>> {
    if (this.cachedUserPromise === null) {
      this.cachedUserPromise = this.fetchCurrentUser();
    }
    return this.cachedUserPromise;
  }

  private async fetchCurrentUser(): Promise<ServiceSuccessResponse<User>> {
    const response = await this.axios.get('/user');
    const dealerships = DealersSchema.parse(response.data.dealerships);
    const authenticatedUser = await msalInstance.getAuthenticatedUser();

    if (authenticatedUser === null) {
      throw new Error('User not authenticated');
    }

    const user = MsalAccountCodec.decode({
      account: authenticatedUser.account,
      scopes: authenticatedUser.scopes,
      dealerships: dealerships,
    });
    return fromAxios(user, response);
  }
}

class UserServiceSingleton implements IUserService {
  private static instance: IUserService | null = null;

  public static init(axios: AxiosInstance): void {
    UserServiceSingleton.instance = new UserService(axios);
  }

  public static mock(mock: IUserService): void {
    UserServiceSingleton.instance = mock;
  }

  private static getInstance(): IUserService {
    if (!UserServiceSingleton.instance) {
      throw new Error('UserService not initialized');
    }
    return UserServiceSingleton.instance;
  }

  getCurrentUser(): Promise<ServiceSuccessResponse<User>> {
    return UserServiceSingleton.getInstance().getCurrentUser();
  }
}

export const InitUserService = (axios: AxiosInstance) =>
  UserServiceSingleton.init(axios);
export const MockUserService = (mock: IUserService) =>
  UserServiceSingleton.mock(mock);
export const userService: IUserService = new UserServiceSingleton();
